import { getLocalStorageUserSha } from '@/utils/storage';
import useAuth from '@rhg/libs/auth0/use-auth';
import {
    queryKeys,
    skipToken,
    useQuery,
    useQueryClient,
} from '@rhg/libs/react-query';
import { useCallback } from 'react';

export const ADD_TO_PLAYLIST_TOOLTIP_SEEN_KEY = 'apts';
/** Hides the tooltip for every member in this browser; the Cypress support hook sets it. */
export const ADD_TO_PLAYLIST_TOOLTIP_DISABLED_KEY = 'aptd';

export const addToPlaylistTooltipSeenQueryKey = (userSub?: string) => [
    queryKeys.addToPlaylistTooltipSeen,
    userSub,
];

// Unreadable storage counts as seen, so the tooltip hides.
async function readHasSeen(userSub: string): Promise<boolean> {
    try {
        const key = await getLocalStorageUserSha(
            ADD_TO_PLAYLIST_TOOLTIP_SEEN_KEY,
            userSub
        );
        return (
            // eslint-disable-next-line no-restricted-syntax -- a seen flag keyed by a hashed user id, no user data
            localStorage.getItem(ADD_TO_PLAYLIST_TOOLTIP_DISABLED_KEY) ===
                'true' ||
            // eslint-disable-next-line no-restricted-syntax -- a seen flag keyed by a hashed user id, no user data
            localStorage.getItem(key) === 'true'
        );
    } catch {
        return true;
    }
}

async function writeHasSeen(userSub: string) {
    try {
        const key = await getLocalStorageUserSha(
            ADD_TO_PLAYLIST_TOOLTIP_SEEN_KEY,
            userSub
        );
        // eslint-disable-next-line no-restricted-syntax -- a seen flag keyed by a hashed user id, no user data
        localStorage.setItem(key, 'true');
    } catch {
        // Storage unavailable: the tooltip stays hidden until the next page load.
    }
}

/** One-time onboarding tooltip for the player's Add to playlist button, once per member. */
export function useAddToPlaylistTooltip() {
    const { user } = useAuth();
    const userSub = user?.sub;
    const queryClient = useQueryClient();
    const { data: hasSeen = true } = useQuery({
        queryKey: addToPlaylistTooltipSeenQueryKey(userSub),
        queryFn: userSub ? () => readHasSeen(userSub) : skipToken,
        staleTime: Infinity,
    });

    const markTooltipSeen = useCallback(() => {
        if (!userSub) {
            return;
        }
        const queryKey = addToPlaylistTooltipSeenQueryKey(userSub);
        // A read still in flight would otherwise land after this and show the tooltip again.
        void queryClient.cancelQueries({ queryKey });
        queryClient.setQueryData(queryKey, true);
        void writeHasSeen(userSub);
    }, [queryClient, userSub]);

    return { isTooltipVisible: !hasSeen, markTooltipSeen };
}
